import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Lightbox from "@/components/Lightbox";
import ShareButton from "@/components/ShareButton";
import LikeButton from "@/components/LikeButton";
import { Loader2, ArrowLeft, Images } from "lucide-react";

export default function CollectionDetailPublic() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    api.get(`/public/collections/${id}`).then((r) => setC(r.data)).catch(() => setC(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <div className="text-center py-24">
          <p className="font-serif text-2xl text-ink">Collection not found</p>
          <Link to="/" className="text-brick font-semibold hover:underline mt-3 inline-block">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      <Header />

      <div className="border-b border-stoneline bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brick mb-4">
            <ArrowLeft className="h-4 w-4" /> All collections
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-brick font-semibold uppercase tracking-widest">
              <Images className="h-4 w-4" /> {c.items.length} images
              {c.category && <span className="text-stone-400">· {c.category}</span>}
            </div>
            <div className="flex items-center gap-2">
              <LikeButton collectionId={c.id} initialLikes={c.likes || 0} initialLiked={!!c.liked} />
              <ShareButton collection={c} />
            </div>
          </div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-serif font-bold text-ink tracking-tight">{c.title}</h1>
          {c.description && <p className="mt-3 text-lg text-muted-foreground max-w-3xl">{c.description}</p>}
          <div className="mt-4 flex items-center gap-2">
            {c.owner_picture && <img src={c.owner_picture} alt="" className="h-8 w-8 rounded-full object-cover border border-stoneline" />}
            <span className="text-sm text-ink">Curated by{" "}
              <Link to={`/curator/${c.owner_id}`} data-testid="curator-profile-link" className="font-semibold text-brick hover:underline">
                {c.owner_name}
              </Link>
            </span>
          </div>
          {(c.tags || []).length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {c.tags.map((t) => <span key={t} className="text-xs text-brick bg-brick/5 px-2.5 py-1 rounded-full">#{t}</span>)}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="masonry columns-1 sm:columns-2 md:columns-3 lg:columns-4">
          {c.items.map((item, i) => (
            <figure
              key={item.item_id}
              data-testid="public-item-card"
              className="group relative rounded-lg overflow-hidden border border-stoneline bg-white shadow-sm hover:shadow-xl transition-all"
            >
              <div onClick={() => setLightbox(i)} className="cursor-zoom-in overflow-hidden">
                <img src={item.image_url} alt={item.title} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-ink/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-parchment text-sm font-medium line-clamp-2">{item.title || "Untitled"}</p>
                  {item.date && <p className="text-parchment/70 text-xs mt-0.5">{item.date}</p>}
                </div>
              </div>
              {item.caption && (
                <figcaption className="p-3 text-sm text-ink/80 italic border-t border-stoneline/70">{item.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>

      {lightbox >= 0 && (
        <Lightbox
          items={c.items}
          index={lightbox}
          onClose={() => setLightbox(-1)}
          onNav={(d) => setLightbox((p) => (p + d + c.items.length) % c.items.length)}
        />
      )}
    </div>
  );
}
